import React, { useEffect, useState } from "react"
import cn from "classnames"
import beats, { bonusBeat } from "shared/constants/beats"
import { getBeatSrc } from "shared/utils/getBeatSrc"
import { isDesktopShell } from "shared/utils/isDesktopShell"
import { isStandaloneApp } from "shared/utils/isStandaloneApp"
import css from "./OfflineDownload.module.scss"

const allBeats = [...beats, bonusBeat]

const STATE = {
  IDLE: "idle",
  CACHING: "caching",
  DONE: "done",
  ERROR: "error"
}

const LABEL = {
  [STATE.IDLE]: "Cache this looper",
  [STATE.DONE]: "Works offline",
  [STATE.ERROR]: "Failed, try again"
}

// Fetching each beat URL is enough to make the service worker's "beats"
// runtime cache route (src/service-worker.js) store it — no custom
// postMessage protocol needed, this just walks the same network path a pad
// click would.
export default function OfflineDownload() {
  const [state, setState] = useState(STATE.IDLE)
  const [progress, setProgress] = useState(0)
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  // React state alone forgets everything on every reload/relaunch, even
  // though the cache itself survives — check what's actually in Cache
  // Storage so a reinstalled/relaunched PWA still shows "Works offline"
  // instead of making the user re-cache tracks it already has.
  useEffect(() => {
    if (!("caches" in window)) return
    let cancelled = false
    ;(async () => {
      try {
        const cache = await caches.open("beats")
        const allCached = await Promise.all(allBeats.map((beat) => cache.match(getBeatSrc(beat))))
        if (!cancelled && allCached.every(Boolean)) setState(STATE.DONE)
      } catch {
        // ignore — worst case the button just starts as "Cache this looper"
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Only useful once the app is installed as a PWA — offline caching in a
  // regular browser tab isn't something people go looking for. Electron and
  // Tauri already have their mp3s on disk as part of the app bundle itself,
  // so there's nothing here for them to cache either.
  if (isDesktopShell() || !("serviceWorker" in navigator) || !isStandaloneApp()) return null

  const handleClick = async () => {
    setState(STATE.CACHING)
    setProgress(0)
    try {
      for (let i = 0; i < allBeats.length; i++) {
        await fetch(getBeatSrc(allBeats[i]))
        setProgress(i + 1)
      }
      setState(STATE.DONE)
    } catch {
      setState(STATE.ERROR)
    }
  }

  // Once cached, re-caching only makes sense back online (offline it would
  // just fail) — and there's nothing new to fetch anyway.
  const isDisabled = state === STATE.CACHING || (state === STATE.DONE && !isOnline)

  return (
    <button
      type="button"
      className={cn(css.button, state === STATE.DONE && css.done)}
      onClick={handleClick}
      disabled={isDisabled}
    >
      {state === STATE.CACHING ? `Downloading… ${progress}/${allBeats.length}` : LABEL[state]}
    </button>
  )
}
